import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-apx-lg text-sm font-medium ring-offset-background transition-all duration-apx-normal ease-apx-ease focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-apx-border bg-transparent hover:bg-apx-bg-hover hover:text-foreground hover:-translate-y-0.5",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-apx-bg-hover hover:text-foreground",
        link: "text-insight-500 underline-offset-4 hover:underline",
        // APX Design System Variants
        insight:
          "bg-gradient-to-r from-insight-500 to-insight-600 text-white shadow-[0_4px_14px_rgba(16,185,129,0.25)] hover:shadow-apx-glow-insight hover:-translate-y-0.5 active:translate-y-0",
        "insight-outline":
          "border border-insight-500/50 text-insight-400 bg-transparent hover:bg-insight-500/10 hover:border-insight-500 hover:-translate-y-0.5",
        "insight-ghost":
          "text-insight-400 bg-transparent hover:bg-insight-500/10 hover:text-insight-300",
        // Tier variants for DDQ review
        success: "bg-tier-exact text-white hover:bg-tier-exact/90 hover:-translate-y-0.5",
        warning: "bg-tier-suggested text-white hover:bg-tier-suggested/90 hover:-translate-y-0.5",
        // APX subtle variant
        subtle:
          "bg-apx-bg-hover text-apx-text-secondary border border-apx-border-subtle hover:bg-apx-bg-active hover:text-apx-text-primary hover:-translate-y-0.5",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-apx-md px-3 text-xs",
        lg: "h-11 rounded-apx-lg px-8 text-base",
        xl: "h-12 rounded-apx-xl px-10 text-base font-semibold",
        icon: "h-10 w-10",
        "icon-sm": "h-8 w-8",
        "icon-lg": "h-12 w-12",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, children, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <>
            <svg
              className="animate-apx-spin -ml-1 mr-2 h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Loading...
          </>
        ) : (
          children
        )}
      </Comp>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
